import React, { useState, useEffect } from 'react';
import { adminAPI } from '../../services/api';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Badge } from '../../components/ui/badge';
import { Switch } from '../../components/ui/switch';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../../components/ui/dialog';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../../components/ui/table';
import { Skeleton } from '../../components/ui/skeleton';
import {
  Plus, Save, Trash2, ChevronUp, ChevronDown, Loader2,
  Heading2, AlignLeft, List, Type, CheckCircle2, Eye, EyeOff, Pencil, AlertCircle
} from 'lucide-react';
import { toast } from 'sonner';

const NAVY = 'hsl(var(--c4p-navy-900))';
const CATEGORIES = ['General', 'Infrastructure', 'Security', 'Licensing', 'Support', 'Pricing'];

// ── Product Inclusions Tab ────────────────────────────────────────────────────
// ── Product Inclusions Tab ───────────────────────────────────────────────────
function ProductFeaturesTab() {
  const [inclusions, setInclusions] = useState([]);
  const [loading, setLoading]       = useState(true);
  const [showDialog, setShowDialog] = useState(false);
  const [editItem, setEditItem]     = useState(null);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleting, setDeleting]     = useState(false);
  const [saving, setSaving]         = useState(false);
  const emptyForm = { label: '', sub_label: '', category: 'General', product: 'ALL', show_on_quote: false, show_on_service_desc: true, sort_order: 99 };
  const [form, setForm] = useState(emptyForm);

  const load = async () => {
    try { const { data } = await adminAPI.getProductInclusions(); setInclusions(data || []); }
    catch { toast.error('Failed to load'); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const openNew = () => { setEditItem(null); setForm({ ...emptyForm, sort_order: inclusions.length + 1 }); setShowDialog(true); };
  const openEdit = (item) => {
    setEditItem(item);
    setForm({ label: item.label, sub_label: item.sub_label || '', category: item.category, product: item.product, show_on_quote: !!item.show_on_quote, show_on_service_desc: !!item.show_on_service_desc, sort_order: item.sort_order ?? 99 });
    setShowDialog(true);
  };

  const handleSave = async (e) => {
    e.preventDefault();
    if (!form.label.trim()) return toast.error('Label is required');
    setSaving(true);
    try {
      if (editItem) { await adminAPI.updateProductInclusion(editItem.id, form); toast.success('Updated'); }
      else          { await adminAPI.createProductInclusion(form); toast.success('Added'); }
      setShowDialog(false); load();
    } catch { toast.error('Failed to save'); } finally { setSaving(false); }
  };

  const handleDelete = async () => {
    if (!deleteTarget) return; setDeleting(true);
    try { await adminAPI.deleteProductInclusion(deleteTarget.id); toast.success('Deleted'); setDeleteTarget(null); load(); }
    catch { toast.error('Failed'); } finally { setDeleting(false); }
  };

  const toggleActive = async (item) => {
    try { await adminAPI.updateProductInclusion(item.id, { is_active: !item.is_active }); load(); }
    catch { toast.error('Failed'); }
  };

  const PRODUCT_COLORS = {
    STANDARD: { bg: 'hsl(var(--muted))',          color: NAVY },
    ADVANCED: { bg: 'hsl(var(--c4p-orange-600))', color: 'white' },
    BOTH:     { bg: 'hsl(196 75% 45%)', color: 'white' },
    MTPRO:    { bg: 'hsl(265 89% 38%)', color: 'white' },
    'ADVANCED|MTPRO': { bg: 'hsl(var(--c4p-orange-600))', color: 'white' },
    'STANDARD|ADVANCED': { bg: NAVY, color: 'white' },
    ALL:      { bg: '#15803d', color: 'white' },
  };
  const PRODUCT_LABELS = { STANDARD: 'Standard', ADVANCED: 'Advanced', MTPRO: 'MTPro', CUSTOM: 'Custom', BOTH: 'Std+Adv', 'STANDARD|ADVANCED': 'Std+Adv', 'ADVANCED|MTPRO': 'Adv+MTPro', 'STANDARD|MTPRO': 'Std+MTPro', 'STANDARD|ADVANCED|MTPRO': 'Std+Adv+MTPro', ALL: 'All Products' };

  const sorted = [...inclusions].sort((a, b) => {
    const po = { ALL: 0, BOTH: 1, 'STANDARD|ADVANCED|MTPRO': 1, 'STANDARD|ADVANCED': 2, 'ADVANCED|MTPRO': 2, 'STANDARD|MTPRO': 2, STANDARD: 3, ADVANCED: 4, MTPRO: 5 };
    if ((po[a.product]??3) !== (po[b.product]??3)) return (po[a.product]??3) - (po[b.product]??3);
    return (a.sort_order??99) - (b.sort_order??99);
  });

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <p className="text-sm" style={{ color: 'hsl(var(--muted-fg))' }}>
          {inclusions.filter(i => i.is_active).length} active &nbsp;&middot;&nbsp;
          <span style={{ color: '#15803d' }}>{inclusions.filter(i => i.show_on_quote && i.is_active).length} on quote</span> &nbsp;&middot;&nbsp;
          <span style={{ color: 'hsl(var(--c4p-brand-blue))' }}>{inclusions.filter(i => i.show_on_service_desc && i.is_active).length} in service description</span>
        </p>
        <Button size="sm" onClick={openNew} style={{ background: 'hsl(var(--c4p-orange-600))', color: 'white' }} className="gap-1.5">
          <Plus size={13} /> Add Feature
        </Button>
      </div>

      <div className="rounded-xl border bg-white overflow-hidden">
        <Table>
          <TableHeader>
            <TableRow style={{ background: 'hsl(var(--muted))' }}>
              <TableHead className="w-8">#</TableHead>
              <TableHead>Feature</TableHead>
              <TableHead>Product</TableHead>
              <TableHead>Category</TableHead>
              <TableHead className="text-center">On Quote</TableHead>
              <TableHead className="text-center">Service Desc.</TableHead>
              <TableHead className="text-center">Active</TableHead>
              <TableHead />
            </TableRow>
          </TableHeader>
          <TableBody>
            {loading ? Array.from({length:5}).map((_,i) => <TableRow key={i}><TableCell colSpan={8}><Skeleton className="h-10 w-full"/></TableCell></TableRow>)
            : sorted.length === 0 ? <TableRow><TableCell colSpan={8} className="text-center py-8 text-sm" style={{color:'hsl(var(--muted-fg))'}}>No features yet</TableCell></TableRow>
            : sorted.map(item => {
              const pc = PRODUCT_COLORS[item.product] || { bg: 'hsl(var(--muted))', color: NAVY };
              return (
                <TableRow key={item.id} style={{ opacity: item.is_active ? 1 : 0.45 }}>
                  <TableCell className="text-xs font-mono" style={{color:'hsl(var(--muted-fg))'}}>{item.sort_order}</TableCell>
                  <TableCell>
                    <p className="text-sm font-semibold">{item.label}</p>
                    {item.sub_label && <p className="text-xs" style={{color:'hsl(var(--muted-fg))'}}>{item.sub_label}</p>}
                  </TableCell>
                  <TableCell>
                    <span className="text-xs font-semibold px-2 py-0.5 rounded-full" style={{background:pc.bg, color:pc.color}}>
                      {PRODUCT_LABELS[item.product] || item.product}
                    </span>
                  </TableCell>
                  <TableCell><Badge variant="outline" className="text-xs">{item.category}</Badge></TableCell>
                  <TableCell className="text-center">
                    {item.show_on_quote
                      ? <span className="text-xs font-medium" style={{color:'#15803d'}}>&#10003; Yes</span>
                      : <span className="text-xs" style={{color:'hsl(var(--muted-fg))'}}>No</span>}
                  </TableCell>
                  <TableCell className="text-center">
                    {item.show_on_service_desc
                      ? <span className="text-xs font-medium" style={{color:'hsl(var(--c4p-brand-blue))'}}>&#10003; Yes</span>
                      : <span className="text-xs" style={{color:'hsl(var(--muted-fg))'}}>No</span>}
                  </TableCell>
                  <TableCell className="text-center">
                    <Switch checked={item.is_active} onCheckedChange={() => toggleActive(item)} />
                  </TableCell>
                  <TableCell>
                    <div className="flex gap-1">
                      <Button variant="ghost" size="sm" onClick={() => openEdit(item)} className="h-7 w-7 p-0"><Pencil size={12}/></Button>
                      <Button variant="ghost" size="sm" onClick={() => setDeleteTarget(item)} className="h-7 w-7 p-0" style={{color:'hsl(var(--danger))'}}><Trash2 size={12}/></Button>
                    </div>
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </div>

      <Dialog open={showDialog} onOpenChange={setShowDialog}>
        <DialogContent className="max-w-md">
          <DialogHeader><DialogTitle>{editItem ? 'Edit Feature' : 'Add Feature'}</DialogTitle></DialogHeader>
          <form onSubmit={handleSave} className="space-y-4">
            <div className="space-y-1.5"><Label>Feature Label *</Label>
              <Input value={form.label} onChange={e => setForm(f=>({...f,label:e.target.value}))} placeholder="e.g. MFA - Multi-Factor Authentication" required /></div>
            <div className="space-y-1.5"><Label>Sub-label / Description</Label>
              <Input value={form.sub_label} onChange={e => setForm(f=>({...f,sub_label:e.target.value}))} placeholder="e.g. Per user, included at no extra cost" /></div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5"><Label>Applies to Product</Label>
                <Select value={form.product} onValueChange={v=>setForm(f=>({...f,product:v}))}>
                  <SelectTrigger><SelectValue/></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="ALL">All Products (Std, Adv, MTPro)</SelectItem>
                    <SelectItem value="STANDARD|ADVANCED|MTPRO">Std + Adv + MTPro</SelectItem>
                    <SelectItem value="STANDARD|ADVANCED">Standard + Advanced</SelectItem>
                    <SelectItem value="ADVANCED|MTPRO">Advanced + MTPro</SelectItem>
                    <SelectItem value="STANDARD|MTPRO">Standard + MTPro</SelectItem>
                    <SelectItem value="STANDARD">Standard only</SelectItem>
                    <SelectItem value="ADVANCED">Advanced only</SelectItem>
                    <SelectItem value="MTPRO">MTPro only</SelectItem>
                    <SelectItem value="CUSTOM">C4P Custom only</SelectItem>
                    <SelectItem value="BOTH">Standard + Advanced (legacy)</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-1.5"><Label>Category</Label>
                <Select value={form.category} onValueChange={v=>setForm(f=>({...f,category:v}))}>
                  <SelectTrigger><SelectValue/></SelectTrigger>
                  <SelectContent>{CATEGORIES.map(c=><SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
                </Select>
              </div>
            </div>
            <div className="space-y-1.5"><Label>Sort Order</Label>
              <Input type="number" min={1} value={form.sort_order} onChange={e=>setForm(f=>({...f,sort_order:parseInt(e.target.value)||99}))}/></div>
            <div className="space-y-3 rounded-xl border p-3">
              <div className="flex items-center justify-between">
                <div><p className="text-sm font-medium">Show on Quote PDF</p>
                  <p className="text-xs" style={{color:'hsl(var(--muted-fg))'}}>Green "Included" row in the pricing table</p></div>
                <Switch checked={form.show_on_quote} onCheckedChange={v=>setForm(f=>({...f,show_on_quote:v}))}/>
              </div>
              <div className="flex items-center justify-between">
                <div><p className="text-sm font-medium">Show on Service Description (Page 2)</p>
                  <p className="text-xs" style={{color:'hsl(var(--muted-fg))'}}>"What's Included" section on PDF page 2</p></div>
                <Switch checked={form.show_on_service_desc} onCheckedChange={v=>setForm(f=>({...f,show_on_service_desc:v}))}/>
              </div>
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={()=>setShowDialog(false)}>Cancel</Button>
              <Button type="submit" disabled={saving} style={{background:NAVY,color:'white'}}>
                {saving ? <Loader2 className="h-4 w-4 animate-spin"/> : editItem ? 'Save Changes' : 'Add Feature'}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      <Dialog open={!!deleteTarget} onOpenChange={()=>setDeleteTarget(null)}>
        <DialogContent className="max-w-sm">
          <DialogHeader><DialogTitle className="flex items-center gap-2"><AlertCircle size={16} style={{color:'hsl(var(--danger))'}}/> Delete Feature</DialogTitle></DialogHeader>
          <p className="text-sm" style={{color:'hsl(var(--muted-fg))'}}>Delete <strong>{deleteTarget?.label}</strong>? This cannot be undone.</p>
          <DialogFooter className="mt-2">
            <Button variant="outline" onClick={()=>setDeleteTarget(null)}>Cancel</Button>
            <Button onClick={handleDelete} disabled={deleting} style={{background:'hsl(var(--danger))',color:'white'}}>
              {deleting ? <Loader2 className="h-4 w-4 animate-spin"/> : 'Delete'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

// ── Block editor & preview (unchanged from original) ──────────────────────────
const BLOCK_TYPES = [
  { type: 'heading',    icon: Heading2,  label: 'Heading',    desc: 'Large section title' },
  { type: 'subheading', icon: Type,      label: 'Sub-heading', desc: 'Section sub-title' },
  { type: 'paragraph',  icon: AlignLeft, label: 'Paragraph',  desc: 'Body text' },
  { type: 'bullets',    icon: List,      label: 'Bullet List', desc: 'Bulleted list items' },
];

const PROD_OPTS = [
  { value: 'ALL',            label: 'All Products' },
  { value: 'STANDARD',       label: 'Standard only' },
  { value: 'ADVANCED',       label: 'Advanced only' },
  { value: 'MTPRO',          label: 'MTPro only' },
  { value: 'CUSTOM',         label: 'Custom only' },
  { value: 'STANDARD|ADVANCED',  label: 'Std + Advanced' },
  { value: 'ADVANCED|MTPRO',     label: 'Advanced + MTPro' },
  { value: 'STANDARD|MTPRO',     label: 'Std + MTPro' },
  { value: 'STANDARD|ADVANCED|MTPRO', label: 'Std + Adv + MTPro' },
];
const DB_OPTS = [
  { value: 'ALL',      label: 'HANA + SQL + Progress 4GL' },
  { value: 'HANA',     label: 'HANA only' },
  { value: 'SQL',      label: 'SQL only' },
  { value: 'PRO4GL',   label: 'Progress 4GL only' },
  { value: 'HANA|SQL', label: 'HANA + SQL only' },
];

const PROD_COLORS = {
  ALL: { bg: '#15803d', color: 'white' },
  STANDARD: { bg: 'hsl(var(--muted))', color: 'hsl(var(--foreground))' },
  ADVANCED: { bg: 'hsl(var(--c4p-orange-600))', color: 'white' },
  MTPRO: { bg: 'hsl(265 89% 38%)', color: 'white' },
  CUSTOM: { bg: 'hsl(var(--c4p-brand-blue))', color: 'white' },
  'STANDARD|ADVANCED': { bg: '#1e3a5f', color: 'white' },
  'ADVANCED|MTPRO': { bg: 'hsl(var(--c4p-orange-600))', color: 'white' },
  'STANDARD|MTPRO': { bg: '#0891b2', color: 'white' },
  'STANDARD|ADVANCED|MTPRO': { bg: '#374151', color: 'white' },
};
const DB_COLORS = {
  ALL:  { bg: '#0891b2', color: 'white' },
  HANA: { bg: '#7c3aed', color: 'white' },
  SQL:  { bg: '#0369a1', color: 'white' },
  PRO4GL: { bg: '#b45309', color: 'white' },
  'HANA|SQL': { bg: '#334155', color: 'white' },
};

// Available template variables for helper panel
const VARIABLES = [
  { v: '{product_name}', eg: 'C4P Advanced' },
  { v: '{db_type}',      eg: 'SAP HANA' },
  { v: '{region_name}',  eg: 'EU (Ireland)' },
  { v: '{num_users}',    eg: '45' },
  { v: '{term_months}',  eg: '36' },
];

function newBlock(type) {
  return {
    id: `${Date.now()}-${Math.random().toString(36).slice(2)}`,
    type,
    content: '',
    items: type === 'bullets' ? [{ text: '', active: true }] : undefined,
    product: 'ALL',
    db_type: 'ALL',
  };
}

// Normalise a bullet item - handles both legacy strings and new {text,active} objects
const normItem = (item) => typeof item === 'string' ? { text: item, active: true } : (item || { text: '', active: true });

function BlockEditor({ block, index, total, onChange, onDelete, onMoveUp, onMoveDown }) {
  const typeInfo = BLOCK_TYPES.find(t => t.type === block.type);
  const Icon = typeInfo?.icon || AlignLeft;
  const updateContent = (val) => onChange({ ...block, content: val });
  const updateItem   = (i, val) => { const items = (block.items||[]).map((it,idx) => idx===i ? { ...normItem(it), text: val } : normItem(it)); onChange({ ...block, items }); };
  const toggleItem   = (i)     => { const items = (block.items||[]).map((it,idx) => idx===i ? { ...normItem(it), active: !normItem(it).active } : normItem(it)); onChange({ ...block, items }); };
  const addItem      = ()      => onChange({ ...block, items: [...(block.items||[]).map(normItem), { text: '', active: true }] });
  const removeItem   = (i)     => { const items = (block.items||[]).map(normItem).filter((_,idx) => idx !== i); onChange({ ...block, items: items.length ? items : [{ text: '', active: true }] }); };

  const prodStyle = PROD_COLORS[block.product || 'ALL'] || PROD_COLORS.ALL;
  const dbStyle   = DB_COLORS[block.db_type   || 'ALL'] || DB_COLORS.ALL;
  const prodLabel = (PROD_OPTS.find(o => o.value === (block.product || 'ALL')) || PROD_OPTS[0]).label;
  const dbLabel   = (DB_OPTS.find(o => o.value === (block.db_type || 'ALL')) || DB_OPTS[0]).label;

  return (
    <div className="group rounded-xl border bg-white overflow-hidden">
      {/* Block header: type + product/db filters + move/delete */}
      <div className="flex items-center gap-2 px-3 py-2 flex-wrap" style={{ background: 'hsl(var(--muted) / 0.4)', borderBottom: '1px solid hsl(var(--border))' }}>
        <Icon size={13} style={{ color: 'hsl(var(--muted-fg))' }} />
        <span className="text-xs font-semibold uppercase tracking-wide" style={{ color: 'hsl(var(--muted-fg))' }}>{typeInfo?.label}</span>

        {/* Product filter */}
        <div className="relative flex-shrink-0">
          <select
            value={block.product || 'ALL'}
            onChange={e => onChange({ ...block, product: e.target.value })}
            className="appearance-none text-xs font-semibold rounded px-1.5 py-0.5 pr-4 cursor-pointer border-0 outline-none"
            style={{ background: prodStyle.bg, color: prodStyle.color, fontSize: '10px' }}
            title="Show this block for which products?"
          >
            {PROD_OPTS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
          </select>
          <span className="pointer-events-none absolute right-1 top-1/2 -translate-y-1/2 text-[8px]" style={{ color: prodStyle.color, opacity: 0.7 }}>▼</span>
        </div>

        {/* DB type filter */}
        <div className="relative flex-shrink-0">
          <select
            value={block.db_type || 'ALL'}
            onChange={e => onChange({ ...block, db_type: e.target.value })}
            className="appearance-none text-xs font-semibold rounded px-1.5 py-0.5 pr-4 cursor-pointer border-0 outline-none"
            style={{ background: dbStyle.bg, color: dbStyle.color, fontSize: '10px' }}
            title="Show this block for which database type?"
          >
            {DB_OPTS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
          </select>
          <span className="pointer-events-none absolute right-1 top-1/2 -translate-y-1/2 text-[8px]" style={{ color: dbStyle.color, opacity: 0.7 }}>▼</span>
        </div>

        <div className="ml-auto flex items-center gap-0.5">
          <Button type="button" variant="ghost" size="sm" className="h-6 w-6 p-0" onClick={onMoveUp}  disabled={index===0}><ChevronUp size={12}/></Button>
          <Button type="button" variant="ghost" size="sm" className="h-6 w-6 p-0" onClick={onMoveDown} disabled={index===total-1}><ChevronDown size={12}/></Button>
          <Button type="button" variant="ghost" size="sm" className="h-6 w-6 p-0" onClick={onDelete} style={{ color: 'hsl(var(--danger))' }}><Trash2 size={11}/></Button>
        </div>
      </div>
      <div className="p-3">
        {block.type === 'heading'    && <input className="w-full text-lg font-bold outline-none border-0 bg-transparent" style={{ color: NAVY }} placeholder="Enter heading..." value={block.content} onChange={e => updateContent(e.target.value)} />}
        {block.type === 'subheading' && <input className="w-full text-sm font-bold outline-none border-0 bg-transparent uppercase tracking-wide" style={{ color: 'hsl(var(--c4p-orange-600))' }} placeholder="Enter sub-heading..." value={block.content} onChange={e => updateContent(e.target.value)} />}
        {block.type === 'paragraph'  && <textarea className="w-full text-sm outline-none border-0 bg-transparent resize-none leading-relaxed" style={{ color: '#334155', minHeight: '60px' }} placeholder="Enter paragraph text..." value={block.content} onChange={e => { updateContent(e.target.value); e.target.style.height='auto'; e.target.style.height=e.target.scrollHeight+'px'; }} onFocus={e => { e.target.style.height='auto'; e.target.style.height=e.target.scrollHeight+'px'; }} rows={3} />}
        {block.type === 'bullets' && (
          <div className="space-y-1.5">
            {(block.items||[{ text: '', active: true }]).map((rawItem, i) => {
              const item = normItem(rawItem);
              return (
                <div key={i} className="flex items-start gap-2">
                  <span className="mt-2 flex-shrink-0 text-sm font-bold" style={{ color: item.active ? 'hsl(var(--c4p-orange-600))' : 'hsl(var(--muted-fg))' }}>•</span>
                  <input
                    className="flex-1 text-sm outline-none border-0 bg-transparent py-1 transition-all"
                    style={{ color: item.active ? '#334155' : 'hsl(var(--muted-fg))', textDecoration: item.active ? 'none' : 'line-through' }}
                    placeholder={`Bullet point ${i+1}...`}
                    value={item.text}
                    onChange={e => updateItem(i, e.target.value)}
                    onKeyDown={e => {
                      if (e.key==='Enter') { e.preventDefault(); addItem(); }
                      if (e.key==='Backspace' && !item.text && (block.items||[]).length>1) { e.preventDefault(); removeItem(i); }
                    }}
                  />
                  {/* Active/inactive toggle */}
                  <Button type="button" variant="ghost" size="sm" className="h-6 w-6 p-0 mt-0.5 flex-shrink-0"
                    title={item.active ? 'Disable this bullet' : 'Enable this bullet'}
                    onClick={() => toggleItem(i)}
                    style={{ color: item.active ? 'hsl(var(--c4p-brand-blue))' : 'hsl(var(--muted-fg))' }}>
                    {item.active ? <Eye size={12}/> : <EyeOff size={12}/>}
                  </Button>
                  {(block.items||[]).length > 1 && (
                    <Button type="button" variant="ghost" size="sm" className="h-6 w-6 p-0 mt-0.5 flex-shrink-0" onClick={() => removeItem(i)} style={{ color: 'hsl(var(--muted-fg))' }}><Trash2 size={10}/></Button>
                  )}
                </div>
              );
            })}
            <Button type="button" variant="ghost" size="sm" className="gap-1 h-6 text-xs mt-1" style={{ color: 'hsl(var(--c4p-brand-blue))' }} onClick={addItem}><Plus size={11}/> Add bullet</Button>
          </div>
        )}
      </div>
    </div>
  );
}

function applyPreviewVars(text, product, dbType) {
  const labels = { STANDARD: 'C4P Standard', ADVANCED: 'C4P Advanced', MTPRO: 'C4P MTPro' };
  const dbLabels = { HANA: 'SAP HANA', SQL: 'Microsoft SQL Server', PRO4GL: 'Progress 4GL' };
  return text
    .replace(/{product_name}/g, labels[product] || product)
    .replace(/{db_type}/g, dbLabels[dbType] || dbType)
    .replace(/{region_name}/g, 'EU (Ireland)')
    .replace(/{num_users}/g, '45')
    .replace(/{term_months}/g, '36');
}

function filterBlocks(blocks, product, dbType) {
  return blocks.filter(b => {
    const bp = b.product || 'ALL';
    const bd = b.db_type || 'ALL';
    if (bp !== 'ALL' && !bp.split('|').includes(product)) return false;
    if (bd !== 'ALL' && !bd.split('|').includes(dbType)) return false;
    return true;
  });
}

function Preview({ blocks, previewProduct, previewDb }) {
  const filtered = filterBlocks(blocks, previewProduct, previewDb);
  return (
    <div className="space-y-3">
      <div className="rounded-xl border bg-white p-5 space-y-2" style={{ maxHeight: '600px', overflowY: 'auto', fontSize: '13px' }}>
        <p className="text-xs font-semibold uppercase tracking-wide mb-4" style={{ color: 'hsl(var(--muted-fg))' }}>
          PDF Preview - {(PROD_OPTS.find(o=>o.value===previewProduct)||PROD_OPTS[0]).label} · {(DB_OPTS.find(o=>o.value===previewDb)||DB_OPTS[0]).label}
          <span className="ml-2 font-normal">({filtered.length}/{blocks.length} blocks shown)</span>
        </p>
        {filtered.map((b,i) => {
          const sub = (t) => applyPreviewVars(t || '', previewProduct, previewDb);
          return (
            <div key={b.id||i}>
              {b.type==='heading'    && <h2 className="font-black mt-4" style={{ color: NAVY, fontSize:'16px' }}>{sub(b.content)||<em style={{opacity:0.3}}>Empty heading</em>}</h2>}
              {b.type==='subheading' && <h3 className="font-bold uppercase tracking-wide mt-3 text-xs" style={{ color: 'hsl(var(--c4p-orange-600))' }}>{sub(b.content)||<em style={{opacity:0.3}}>Empty sub-heading</em>}</h3>}
              {b.type==='paragraph'  && <p className="leading-relaxed text-sm" style={{ color: '#334155' }}>{sub(b.content)||<em style={{opacity:0.3}}>Empty paragraph</em>}</p>}
              {b.type === 'bullets' && <ul className="space-y-0.5 pl-0">{(b.items||[]).map((rawItem,j) => {
                const it = normItem(rawItem);
                return (
                  <li key={j} className="flex items-start gap-2 text-sm" style={{ color: it.active ? '#334155' : 'hsl(var(--muted-fg))', opacity: it.active ? 1 : 0.4, textDecoration: it.active ? 'none' : 'line-through' }}>
                    <span className="font-bold flex-shrink-0 mt-0.5" style={{ color: it.active ? 'hsl(var(--c4p-orange-600))' : 'hsl(var(--muted-fg))' }}>•</span>
                    <span>{sub(it.text)||<em style={{opacity:0.3}}>Empty bullet</em>}</span>
                  </li>
                );
              })}</ul>}
            </div>
          );
        })}
        {filtered.length===0 && <p className="text-sm italic" style={{ color: 'hsl(var(--muted-fg))' }}>No blocks match this product + DB combination.</p>}
      </div>
    </div>
  );
}

// ── Service Description Builder Tab ──────────────────────────────────────────
function BuilderTab() {
  const [blocks, setBlocks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving]   = useState(false);
  const [saved, setSaved]     = useState(false);
  const [showPreview, setShowPreview] = useState(true);
  const [previewProduct, setPreviewProduct] = useState('ADVANCED');
  const [previewDb, setPreviewDb]           = useState('HANA');
  const [showVars, setShowVars]             = useState(false);

  useEffect(() => {
    adminAPI.getServiceDescription().then(r => { setBlocks(r.data.blocks || []); })
      .catch(() => toast.error('Failed to load service description'))
      .finally(() => setLoading(false));
  }, []);

  const updateBlock  = (i, b) => setBlocks(prev => prev.map((x,idx) => idx===i ? b : x));
  const deleteBlock  = (i)    => setBlocks(prev => prev.filter((_,idx) => idx!==i));
  const moveUp       = (i)    => { if (i===0) return; const a=[...blocks]; [a[i-1],a[i]]=[a[i],a[i-1]]; setBlocks(a); };
  const moveDown     = (i)    => { if (i>=blocks.length-1) return; const a=[...blocks]; [a[i],a[i+1]]=[a[i+1],a[i]]; setBlocks(a); };
  const addBlock     = (type) => setBlocks(prev => [...prev, newBlock(type)]);

  const handleSave = async () => {
    setSaving(true); setSaved(false);
    try { await adminAPI.saveServiceDescription({ blocks }); toast.success('Service description saved'); setSaved(true); setTimeout(()=>setSaved(false),3000); }
    catch { toast.error('Failed to save'); }
    finally { setSaving(false); }
  };

  return (
    <div className="space-y-4">
      {/* Top bar: description + preview toggle + save */}
      <div className="flex items-center justify-between flex-wrap gap-3">
        <p className="text-sm" style={{ color: 'hsl(var(--muted-fg))' }}>
          Each block can target specific products and/or database types. Use <code className="text-xs px-1 rounded" style={{background:'hsl(var(--muted))'}}>{'{variables}'}</code> for dynamic content.
        </p>
        <div className="flex items-center gap-2">
          <Button variant="outline" size="sm" onClick={() => setShowPreview(v => !v)} className="gap-1.5">
            {showPreview ? <EyeOff size={13}/> : <Eye size={13}/>}
            {showPreview ? 'Hide Preview' : 'Preview'}
          </Button>
          <Button onClick={handleSave} disabled={saving} style={{ background: NAVY, color: 'white' }} className="gap-2">
            {saving ? <Loader2 className="h-4 w-4 animate-spin"/> : <Save size={14}/>} Save
          </Button>
          {saved && <span className="flex items-center gap-1 text-sm" style={{ color: '#15803d' }}><CheckCircle2 size={14}/> Saved</span>}
        </div>
      </div>

      {/* Variables reference */}
      <div className="rounded-lg border" style={{ borderColor: 'hsl(var(--border))' }}>
        <button className="w-full flex items-center justify-between px-4 py-2.5 text-left text-xs font-semibold uppercase tracking-wide"
          style={{ color: 'hsl(var(--muted-fg))' }}
          onClick={() => setShowVars(v => !v)}>
          <span>Available template variables</span>
          <span>{showVars ? '▲' : '▼'}</span>
        </button>
        {showVars && (
          <div className="px-4 pb-3 grid grid-cols-2 sm:grid-cols-3 gap-2">
            {VARIABLES.map(({ v, eg }) => (
              <div key={v} className="rounded px-2 py-1.5 text-xs" style={{ background: 'hsl(var(--muted))' }}>
                <code style={{ color: '#0891b2', fontWeight: 600 }}>{v}</code>
                <span className="ml-2" style={{ color: 'hsl(var(--muted-fg))' }}>e.g. {eg}</span>
              </div>
            ))}
          </div>
        )}
      </div>

      {loading ? <div className="space-y-3">{[1,2,3].map(i=><Skeleton key={i} className="h-20 w-full"/>)}</div> : (
        <div className={`grid gap-6 ${showPreview ? 'grid-cols-2' : 'grid-cols-1 max-w-2xl'}`}>
          {/* Builder column */}
          <div className="space-y-3">
            {blocks.length===0 && <div className="rounded-xl border border-dashed p-8 text-center" style={{ color: 'hsl(var(--muted-fg))' }}><p className="text-sm">No blocks yet.</p></div>}
            {blocks.map((block,i) => <BlockEditor key={block.id||i} block={block} index={i} total={blocks.length} onChange={b=>updateBlock(i,b)} onDelete={()=>deleteBlock(i)} onMoveUp={()=>moveUp(i)} onMoveDown={()=>moveDown(i)} />)}
            <div className="rounded-xl border bg-white p-3">
              <p className="text-xs font-semibold uppercase tracking-wide mb-2.5" style={{ color: 'hsl(var(--muted-fg))' }}>Add Block</p>
              <div className="grid grid-cols-2 gap-2">
                {BLOCK_TYPES.map(bt => { const BIcon = bt.icon; return (
                  <button key={bt.type} type="button" onClick={() => addBlock(bt.type)}
                    className="flex items-center gap-2 px-3 py-2 rounded-lg border text-left transition-all" style={{ borderColor: 'hsl(var(--border))' }}
                    onMouseEnter={e=>{e.currentTarget.style.borderColor='hsl(var(--c4p-brand-blue))'; e.currentTarget.style.background='hsl(196 75% 56% / 0.05)';}}
                    onMouseLeave={e=>{e.currentTarget.style.borderColor='hsl(var(--border))'; e.currentTarget.style.background='white';}}>
                    <BIcon size={14} style={{ color: 'hsl(var(--c4p-brand-blue))', flexShrink: 0 }} />
                    <div><p className="text-xs font-semibold" style={{ color: NAVY }}>{bt.label}</p><p className="text-xs" style={{ color: 'hsl(var(--muted-fg))' }}>{bt.desc}</p></div>
                  </button>
                );})}
              </div>
            </div>
          </div>

          {/* Preview column */}
          {showPreview && (
            <div className="space-y-3">
              {/* Filter controls */}
              <div className="flex items-center gap-2 flex-wrap">
                <span className="text-xs font-semibold" style={{ color: 'hsl(var(--muted-fg))' }}>Preview for:</span>
                <select value={previewProduct} onChange={e => setPreviewProduct(e.target.value)}
                  className="text-xs rounded-lg px-2 py-1 border" style={{ borderColor: 'hsl(var(--border))' }}>
                  <option value="STANDARD">C4P Standard</option>
                  <option value="ADVANCED">C4P Advanced</option>
                  <option value="MTPRO">C4P MTPro</option>
                </select>
                <select value={previewDb} onChange={e => setPreviewDb(e.target.value)}
                  className="text-xs rounded-lg px-2 py-1 border" style={{ borderColor: 'hsl(var(--border))' }}>
                  <option value="HANA">SAP HANA</option>
                  <option value="SQL">SQL Server</option>
                  <option value="PRO4GL">Progress 4GL</option>
                </select>
              </div>
              <Preview blocks={blocks} previewProduct={previewProduct} previewDb={previewDb} />
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// ── Main page ─────────────────────────────────────────────────────────────────
export default function ServiceDescriptionAdmin() {
  return (
    <div className="space-y-5" data-testid="service-description-admin">
      <div>
        <h2 className="text-xl font-bold" style={{ fontFamily: 'var(--font-display)', color: NAVY }}>Service Description & Product Features</h2>
        <p className="text-sm mt-0.5" style={{ color: 'hsl(var(--muted-fg))' }}>
          Manage what’s included in each product and build the service description that appears on PDF quotes.
        </p>
      </div>

      <Tabs defaultValue="features">
        <TabsList className="mb-4">
          <TabsTrigger value="features">Product Features</TabsTrigger>
          <TabsTrigger value="builder">Service Description Builder</TabsTrigger>
        </TabsList>
        <TabsContent value="features"><ProductFeaturesTab /></TabsContent>
        <TabsContent value="builder"><BuilderTab /></TabsContent>
      </Tabs>
    </div>
  );
}
